import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  checkFeatureUsage,
  computeIsLocked,
  computeTrialDaysRemaining,
  isUnlimitedPlan,
  STARTER_DEFAULT_FEATURES,
} from 'src/services/subscriptionGuard';
import { SubscriptionPlan, subscriptionService, UsageRecord, UserSubscription } from 'src/services/subscriptionService';
import { getSupabase } from 'src/utils/supabase';

const STARTER_FALLBACK_PLAN: SubscriptionPlan = {
  id: '',
  name: 'starter',
  display_name: 'Starter',
  price_usd_cents: 19900,
  price_cad_cents: 24900,
  trial_days: 30,
  is_contact_only: false,
  features: STARTER_DEFAULT_FEATURES,
  sort_order: 1,
};

function buildFallbackSubscription(): UserSubscription {
  return {
    id: '',
    user_id: '',
    plan_id: '',
    status: 'trialing',
    stripe_customer_id: null,
    stripe_subscription_id: null,
    currency: 'usd',
    trial_start: null,
    trial_end: null,
    current_period_start: null,
    current_period_end: null,
    cancel_at_period_end: false,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    upgrade_offer_plan_id: null,
    upgrade_offer_coupon_id: null,
    upgrade_offer_currency: null,
    upgrade_offer_approved_at: null,
    upgrade_offer_approved_by: null,
    upgrade_requested_at: null,
    subscription_plans: STARTER_FALLBACK_PLAN,
  };
}

interface SubscriptionContextType {
  subscription: UserSubscription | null;
  plan: SubscriptionPlan | null;
  usage: UsageRecord[];
  loading: boolean;
  isTrialing: boolean;
  trialDaysRemaining: number;
  isLocked: boolean;
  isProfessional: boolean;
  hasUpgradeOffer: boolean;
  hasRequestedUpgrade: boolean;
  canUseFeature: (featureKey: string) => { allowed: boolean; current: number; limit: number };
  incrementLocalUsage: (featureKey: string) => void;
  refreshSubscription: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

interface SubscriptionProviderProps {
  children: ReactNode;
}

export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({ children }) => {
  const [subscription, setSubscription] = useState<UserSubscription | null>(null);
  const [usage, setUsage] = useState<UsageRecord[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchSubscription = useCallback(async () => {
    setLoading(true);
    const [subResult, usageResult] = await Promise.allSettled([
      subscriptionService.getCurrentSubscription(),
      subscriptionService.getUsageStats(),
    ]);
    setSubscription(subResult.status === 'fulfilled' ? subResult.value : null);
    setUsage(usageResult.status === 'fulfilled' ? usageResult.value : []);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  useEffect(() => {
    let cleanup: (() => void) | null = null;
    let cancelled = false;
    (async () => {
      const supabase = await getSupabase();
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user || cancelled) return;
      const channel = supabase
        .channel(`user_sub_${user.id}`)
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'user_subscriptions', filter: `user_id=eq.${user.id}` },
          () => {
            fetchSubscription();
          }
        )
        .subscribe();
      cleanup = () => {
        supabase.removeChannel(channel);
      };
    })();
    return () => {
      cancelled = true;
      if (cleanup) cleanup();
    };
  }, [fetchSubscription]);

  const realPlan = subscription?.subscription_plans || null;
  const treatAsTrialing = subscription?.status === 'trialing' || (!subscription && !loading);
  const plan = realPlan || (treatAsTrialing ? STARTER_FALLBACK_PLAN : null);
  const effectiveSubscription = subscription ?? (treatAsTrialing ? buildFallbackSubscription() : null);

  const isTrialing = treatAsTrialing;

  const trialDaysRemaining = useMemo(
    () => computeTrialDaysRemaining(subscription?.status || '', subscription?.trial_end || null),
    [subscription?.status, subscription?.trial_end]
  );

  const isLocked = useMemo(
    () => (subscription ? computeIsLocked(subscription.status, subscription.trial_end) : false),
    [subscription]
  );

  const isProfessional = plan ? isUnlimitedPlan(plan.name) : false;

  const hasUpgradeOffer = !!subscription?.upgrade_offer_approved_at && !isProfessional;

  const hasRequestedUpgrade = !!subscription?.upgrade_requested_at && !isProfessional && !hasUpgradeOffer;

  const incrementLocalUsage = useCallback((featureKey: string) => {
    setUsage((prev) => {
      const existing = prev.find((u) => u.feature_key === featureKey);
      if (existing) {
        return prev.map((u) => (u.feature_key === featureKey ? { ...u, count: u.count + 1 } : u));
      }
      const now = new Date();
      const periodStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
      const periodEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
      return [...prev, { feature_key: featureKey, count: 1, period_start: periodStart, period_end: periodEnd }];
    });
  }, []);

  const canUseFeature = useCallback(
    (featureKey: string) => checkFeatureUsage(plan?.name, plan?.features, featureKey, usage, isLocked),
    [plan, usage, isLocked]
  );

  const value: SubscriptionContextType = useMemo(
    () => ({
      subscription: effectiveSubscription,
      plan,
      usage,
      loading,
      isTrialing,
      trialDaysRemaining,
      isLocked,
      isProfessional,
      hasUpgradeOffer,
      hasRequestedUpgrade,
      canUseFeature,
      incrementLocalUsage,
      refreshSubscription: fetchSubscription,
    }),
    [
      effectiveSubscription,
      plan,
      usage,
      loading,
      isTrialing,
      trialDaysRemaining,
      isLocked,
      isProfessional,
      hasUpgradeOffer,
      hasRequestedUpgrade,
      canUseFeature,
      incrementLocalUsage,
      fetchSubscription,
    ]
  );

  return <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>;
};
